<script setup lang="ts">
  import { z } from 'zod';
  import { MpButton, MpCard, MpDivider, MpInput, MpText } from '@borg/ui';

  const emit = defineEmits<{
    'resend-verification': [];
    next: [code: string];
  }>();

  const { t } = useI18n();
  const applicationForm = _applicationForm.inject();

  const destination = computed(() => {
    if (applicationForm.application?.data.email) {
      return applicationForm.application.data.email;
    } else if (applicationForm.application?.data.phone) {
      return `+${applicationForm.application.data.phone.countryCode} ${applicationForm.application.data.phone.number}`;
    }

    return '';
  });

  const { values, state, validate } = useForm({
    initialData: () => ({
      code: '',
    }),
    schema: z.object({
      code: z
        .string()
        .min(5, t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.CODE_LENGTH_ERROR', { n: 5 })),
    }),
  });

  async function onResend() {
    emit('resend-verification');
  }

  async function onVerify() {
    if (await validate()) {
      emit('next', values.code);
    }
  }
</script>

<template>
  <MpCard
    border
    shadow
    class="verify-container"
  >
    <MpText
      as="h5"
      uppercase
    >
      {{ $t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.TITLE') }}
    </MpText>
    <MpText>
      {{ $t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.DESCRIPTION') }}
    </MpText>
    <MpText bold> &nbsp;{{ destination }}</MpText>
    <div class="verify-container__input">
      <MpInput
        v-model="values.code"
        required
        test="application-verify-code-input"
        :label="$t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.VERIFY_CODE_LABEL')"
        :error="state.code.message || applicationForm.invalidCodeError"
        size="lg"
        @input="applicationForm.invalidCodeError = ''"
      />
    </div>
    <MpButton
      test="application-verify-button"
      :is-loading="applicationForm.completeStatus === 'inProgress'"
      :text="$t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.VERIFY_BUTTON_LABEL')"
      size="lg"
      @click="onVerify"
    />
    <MpDivider margin="xs" />
    <MpText no-margin>
      <i18n-t
        keypath="JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION.TEXT"
        scope="global"
      >
        <template #button>
          <MpText
            link
            no-margin
            semibold
            :link-disabled="applicationForm.resendVerificationCounterRunning"
            @click="onResend"
          >
            {{ $t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION.BUTTON') }}
          </MpText>
        </template>
        <template
          v-if="applicationForm.resendVerificationCounterRunning"
          #try_again
        >
          <MpText no-margin>
            {{
              $t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION.TRY_AGAIN', {
                seconds: applicationForm.resendVerificationCounter,
              })
            }}
          </MpText>
        </template>
      </i18n-t>
    </MpText>
  </MpCard>
</template>

<style scoped lang="scss">
  .verify-container {
    &__input {
      margin: var(--mp-space-50) 0;
    }
  }
</style>
